<template>
    <div>
        <div class="text-center mb-6">
            <v-icon size="48" color="primary" class="mb-2">mdi-access-point</v-icon>
            <h3 class="text-h6 font-weight-bold">Activate Your Device</h3>
            <p class="text-body-2 text-grey-darken-1">Enter the serial number and the last 5 digits of the IMEI, both on your device label or its packaging</p>
        </div>

        <v-form v-model="formValid" validate-on="input lazy" @submit.prevent="activate">
            <v-alert v-if="errors.length" class="mb-4" type="error" density="compact" variant="tonal" closable>
                <div v-for="error in errors" :key="error">{{ error }}</div>
            </v-alert>

            <v-text-field
                ref="serialInput"
                v-model="serial"
                :rules="[v => !!v || 'Serial number is required', v => (v && v.length >= 5) || 'Must be at least 5 characters']"
                label="Device Serial Number"
                placeholder="Enter serial number"
                prepend-inner-icon="mdi-barcode"
                inputmode="numeric"
                variant="outlined"
                class="mb-2"
                autofocus
                @update:modelValue="onSerialChanged"
            />

            <v-text-field
                v-model="imeiLast5"
                :rules="[v => !!v || 'Required', v => /^\d{5,15}$/.test(v || '') || 'Enter the last 5 digits']"
                label="Last 5 digits of IMEI"
                placeholder="12345"
                prepend-inner-icon="mdi-barcode"
                inputmode="numeric"
                autocomplete="off"
                maxlength="15"
                variant="outlined"
                class="mb-2"
                hint="The label shows the full IMEI. Enter just the last 5 digits."
                persistent-hint
                @update:modelValue="onImeiChanged"
            />

            <div v-if="loadingPlans" class="d-flex align-center ga-2 py-3">
                <v-progress-circular :size="18" :width="2" indeterminate color="primary"/>
                <span class="text-grey text-caption">Loading plans for this device...</span>
            </div>
            <v-alert v-else-if="plansError" density="compact" type="warning" variant="tonal" class="mb-3">
                {{ plansError }}
            </v-alert>
            <v-select
                v-else-if="plans.length > 0"
                v-model="selectedPlan"
                :items="plans"
                :item-title="(plan) => plan.name + (formatPlanPrice(plan) ? ' - ' + formatPlanPrice(plan) : '')"
                item-value="id"
                label="Subscription Plan"
                prepend-inner-icon="mdi-card-account-details"
                variant="outlined"
                class="mb-2"
            >
                <template #item="{props: itemProps, item}">
                    <v-list-item v-bind="itemProps" :subtitle="formatPlanPrice(item.raw)"/>
                </template>
            </v-select>
            <div v-else-if="!canLookUpPlans" class="text-caption text-grey py-2 mb-2">
                Enter the serial number and IMEI last 5 to load available plans
            </div>

            <v-text-field
                v-model="nickname"
                :rules="[v => !!v || 'Nickname is required', v => (v && v.length >= 3) || 'At least 3 characters']"
                label="Device Nickname"
                placeholder="e.g. My Car, Kids Backpack"
                prepend-inner-icon="mdi-label-outline"
                variant="outlined"
                class="mb-2"
            />

            <v-expansion-panels variant="accordion" class="mb-4">
                <v-expansion-panel>
                    <v-expansion-panel-title class="text-body-2 py-2">
                        <v-icon size="18" class="mr-2">mdi-tag-outline</v-icon>
                        Have a discount code?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <v-text-field
                            v-model="discountCode"
                            label="Discount Code"
                            variant="outlined"
                            density="compact"
                            hide-details
                        />
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>

            <v-btn
                type="submit"
                color="primary"
                size="large"
                block
                :disabled="!formValid || !selectedPlan || !nickname || processing"
                :loading="processing"
            >
                <v-icon start>mdi-power</v-icon>
                Activate Device
            </v-btn>
        </v-form>

        <div class="buy-device-cta mt-6 pa-5 text-center rounded-xl">
            <p class="text-body-2 text-grey-darken-2 mb-3">Don't have a tracking device yet?</p>
            <v-btn
                href="https://simpligps.com/shop-gps-trackers/"
                target="_blank"
                color="deep-purple-accent-4"
                variant="tonal"
                size="large"
                rounded="pill"
                class="px-8"
            >
                <v-icon start>mdi-shopping</v-icon>
                Shop Devices
                <v-icon end size="16">mdi-open-in-new</v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, onBeforeUnmount, nextTick} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const emit = defineEmits(['activated', 'paymentRequired']);
const snackbar = useSnackbarStore();

const serialInput = ref(null);
const serial = ref('');
const imeiLast5 = ref('');
const nickname = ref('');
const selectedPlan = ref(null);
const discountCode = ref('');
const formValid = ref(false);
const processing = ref(false);
const errors = ref([]);
const plans = ref([]);
const loadingPlans = ref(false);
const plansError = ref(null);
let debounceTimer = null;

onMounted(() => {
    nextTick(() => serialInput.value?.focus());
});

onBeforeUnmount(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
});

// Whatever was typed or pasted, the API only ever compares the last 5.
const imeiLast5Digits = () => (imeiLast5.value || '').toString().slice(-5);

const canLookUpPlans = computed(
    () => (serial.value || '').toString().trim().length >= 5
        && /^\d{5,15}$/.test(imeiLast5.value || '')
);

// Both fields feed the lookup, so either one changing has to restart it.
const schedulePlanLookup = () => {
    plans.value = [];
    selectedPlan.value = null;
    plansError.value = null;
    loadingPlans.value = false;
    if (debounceTimer) clearTimeout(debounceTimer);
    if (canLookUpPlans.value) {
        const slno = (serial.value || '').toString().trim();
        debounceTimer = setTimeout(() => fetchPlans(slno), 600);
    }
};

const onSerialChanged = (value) => {
    const digitsOnly = (value || '').toString().replace(/[^0-9]/g, '');
    if (digitsOnly !== value) {
        serial.value = digitsOnly;
        return;
    }
    schedulePlanLookup();
};

const onImeiChanged = (value) => {
    const digits = (value || '').toString().replace(/\D/g, '').slice(0, 15);
    if (digits !== value) {
        imeiLast5.value = digits;
        return;
    }
    schedulePlanLookup();
};

const fetchPlans = (slno) => {
    loadingPlans.value = true;
    plansError.value = null;
    send('get', 'subscription-plans/list', {slno, imei_last5: imeiLast5Digits()})
        .then((response) => {
            const data = response.data?.data;
            plans.value = Array.isArray(data) ? data : (data?.data || []);
            if (plans.value.length === 0) {
                plansError.value = 'No plans available for this device serial';
            } else if (plans.value.length === 1) {
                selectedPlan.value = plans.value[0].id;
            }
        })
        .catch((error) => {
            plansError.value = error.response?.data?.message || 'Failed to load subscription plans';
        })
        .finally(() => {
            loadingPlans.value = false;
        });
};

const formatPlanPrice = (plan) => {
    const price = plan.price;
    if (!price || price === '0' || price === '0.00') return 'Free';
    const intervals = {0: '/month', 1: '/quarter', 2: '/year'};
    return `$${price}${intervals[plan.interval] || ''}`;
};

const activate = () => {
    errors.value = [];
    processing.value = true;
    const data = {
        slno: serial.value,
        imei_last5: imeiLast5Digits(),
        plan: selectedPlan.value,
        label: nickname.value,
    };
    if (discountCode.value) data.discount_code = discountCode.value;

    // Personal role is permission-gated (can:permission) to the personal
    // route, not the fleet devices.activateDevice route. Use the personal
    // tracker endpoint (same one the add-tracker dialog and mobile app use);
    // it forwards to the same activateDevice logic, honours bypass_payment,
    // and still returns 303 when a paid device needs a card.
    send('post', 'personal-trackers/register', data)
        .then((response) => {
            if (response.data.success) {
                const device = response.data.data.device;
                snackbar.showSnackbar(response.data.message);
                emit('activated', {device, nickname: nickname.value});
            } else {
                errors.value.push(response.data.message || 'Activation failed. Please try again.');
            }
        })
        .catch((errorResponse) => {
            if (errorResponse.response?.status === 303) {
                emit('paymentRequired');
                return;
            }
            if (errorResponse.response?.data?.errors) {
                const validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data?.message) {
                errors.value.push(errorResponse.response.data.message);
            } else {
                errors.value.push('Unable to activate device. Please check your connection and try again.');
            }
            snackbar.showSnackbar('Activation failed', 'error');
        })
        .finally(() => {
            processing.value = false;
        });
};
</script>

<style scoped>
.buy-device-cta {
    background: linear-gradient(135deg, #f3f0ff 0%, #ede7ff 100%);
    border: 1px dashed #b39ddb;
}
</style>
